import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBookmark, FiMoreVertical, FiEdit2, FiTrash2, FiPrinter, FiSearch, FiPlus } from 'react-icons/fi';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import { getUserCustomSets, getUserCustomDecks, deleteCustomDeck } from '../services/firestoreService';
import { DECKS } from '../data/stimuli';
import { useTranslation } from '../i18n/useTranslation';
import '../styles/deck-library.css';

const DeckCover = ({ src, emoji, color, fallbackIcon }) => {
  const [failed, setFailed] = useState(false);
  const showImg = src && !failed;
  return (
    <div className="browse-card-cover" style={{ background: color || 'var(--bg)' }}>
      {showImg ? (
        <img src={src} alt="" draggable={false} onError={() => setFailed(true)} />
      ) : emoji ? (
        <span className="browse-card-cover-emoji">{emoji}</span>
      ) : (
        fallbackIcon
      )}
    </div>
  );
};

export const DeckLibraryPage = () => {
  const navigate  = useNavigate();
  const { currentUser } = useAuth();
  const { t, tc } = useTranslation();

  const [myDecks, setMyDecks]             = useState([]);
  const [myLoading, setMyLoading]         = useState(true);
  const [searchQuery, setSearchQuery]     = useState('');
  const [openDeckMenu, setOpenDeckMenu]   = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting]           = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    setMyLoading(true);

    Promise.all([
      getUserCustomSets(currentUser.uid),
      getUserCustomDecks(currentUser.uid),
    ]).then(([setsRes, decksRes]) => {
      const sets  = setsRes.success  ? setsRes.data  : [];
      const decks = decksRes.success ? decksRes.data : [];

      // Normalize: customSets use `name`, customDecks use `title`
      const normalizedDecks = decks.map(d => ({ ...d, name: d.name || d.title }));

      // Merge, dedup by id
      const seen = new Set();
      const merged = [...sets, ...normalizedDecks].filter(d => {
        if (seen.has(d.id)) return false;
        seen.add(d.id);
        return true;
      });

      setMyDecks(merged);
      setMyLoading(false);
    });
  }, [currentUser]);

  const handleDeleteConfirmed = async () => {
    if (deleting || !confirmDelete) return;
    setDeleting(true);
    await deleteCustomDeck(confirmDelete.id);
    setMyDecks(prev => prev.filter(d => d.id !== confirmDelete.id));
    setConfirmDelete(null);
    setDeleting(false);
  };

  useEffect(() => {
    if (!openDeckMenu) return;
    const close = () => setOpenDeckMenu(null);
    const id = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(id); document.removeEventListener('click', close); };
  }, [openDeckMenu]);

  // ── One combined, searchable pool of every deck we have ──────────────────
  const combined = [
    ...myDecks.map((d) => ({
      id: d.id,
      name: tc(d.name || d.title) || t('lib.untitledDeck'),
      coverImage: d.coverImage,
      emoji: d.emoji,
      color: d.color,
      meta: `${d.cardCount ?? 0} ${t('dash.cards')}`,
      route: `/session/${encodeURIComponent(d.name || d.title || d.id)}?deckId=${d.id}`,
      source: 'custom',
      isNew: isNew(d),
    })),
    ...DECKS.map((d) => ({
      id: d.name,
      name: tc(d.name),
      coverImage: d.coverImage,
      emoji: d.emoji,
      color: d.color,
      meta: `${d.items?.length ?? 0} ${t('lib.items')}`,
      route: `/session/${encodeURIComponent(d.name)}`,
      source: 'official',
    })),
  ];

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filtered = normalizedQuery
    ? combined.filter((deck) => deck.name.toLowerCase().includes(normalizedQuery))
    : combined;

  return (
    <Layout>
      <div className="browse-page">
        <div className="page-header">
          <h1 className="deck-page-title">{t('lib.title')}</h1>
          <p>{t('lib.subtitle')}</p>
        </div>

        <div className="browse-search-wrap">
          <FiSearch size={16} className="browse-search-icon" />
          <input
            type="text"
            className="browse-search-input"
            placeholder={t('lib.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="browse-section-header">
          <span className="browse-section-count">
            {myLoading ? '…' : `${filtered.length} ${t('dash.decks')}`}
          </span>
          <button className="browse-create-btn" onClick={() => navigate('/create-set')}>
            <FiPlus size={15} /> {t('lib.newDeck')}
          </button>
        </div>

        {/* Delete confirmation modal */}
        {confirmDelete && (
          <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.55)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
            onClick={() => setConfirmDelete(null)}
          >
            <div style={{ backgroundColor:'#FFFFFF', borderRadius:16, padding:'28px 24px', maxWidth:340, width:'100%', boxShadow:'0 24px 64px rgba(0,0,0,0.45)', border:'1.5px solid #E5E7EB' }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ fontSize:17, fontWeight:700, color:'var(--text-primary)', marginBottom:8 }}>{t('dash.deleteTitle')} "{confirmDelete.name}"?</h3>
              <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:24, lineHeight:1.5 }}>{t('dash.deleteBody')}</p>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => setConfirmDelete(null)} style={{ flex:1, height:42, background:'var(--bg)', border:'1.5px solid var(--border)', borderRadius:10, fontSize:14, fontWeight:600, color:'var(--text-secondary)', cursor:'pointer', fontFamily:'inherit' }}>{t('dash.cancel')}</button>
                <button onClick={handleDeleteConfirmed} disabled={deleting} style={{ flex:1, height:42, background:'#DC2626', border:'none', borderRadius:10, fontSize:14, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'inherit', opacity: deleting ? 0.6 : 1 }}>
                  {deleting ? t('dash.deleting') : t('dash.deleteTitle')}
                </button>
              </div>
            </div>
          </div>
        )}

        {myLoading ? (
          <div className="browse-loading">{t('lib.loadingDecks')}</div>
        ) : filtered.length === 0 ? (
          <div className="browse-empty">
            <FiBookmark size={36} />
            <p>{t('lib.noResults')}</p>
          </div>
        ) : (
          <div className="browse-grid">
            {filtered.map((deck) => {
              const menuOpen = openDeckMenu === deck.id;
              return (
                <div
                  key={`${deck.source}-${deck.id}`}
                  className={`browse-card${deck.isNew ? ' browse-card-new' : ''}`}
                  style={{ position:'relative' }}
                  onClick={() => navigate(deck.route)}
                >
                  {deck.isNew && <span className="browse-new-badge">New</span>}

                  {deck.source === 'custom' && (
                    <div style={{ position:'absolute', top:10, right:10, zIndex:10 }} onClick={e => e.stopPropagation()}>
                      <button
                        className="browse-dots-btn"
                        onClick={e => { e.stopPropagation(); setOpenDeckMenu(prev => prev === deck.id ? null : deck.id); }}
                        title={t('lib.options')}
                      >
                        <FiMoreVertical size={15} />
                      </button>
                      {menuOpen && (
                        <div className="browse-deck-menu" onClick={e => e.stopPropagation()}>
                          <button className="browse-menu-item" onClick={() => { setOpenDeckMenu(null); navigate('/create-deck', { state: { editDeckId: deck.id } }); }}>
                            <FiEdit2 size={13} /> {t('dash.edit')}
                          </button>
                          <button className="browse-menu-item browse-menu-item--danger" onClick={() => { setOpenDeckMenu(null); setConfirmDelete({ id: deck.id, name: deck.name }); }}>
                            <FiTrash2 size={13} /> {t('dash.delete')}
                          </button>
                          <button className="browse-menu-item" onClick={() => { setOpenDeckMenu(null); window.print(); }}>
                            <FiPrinter size={13} /> {t('dash.print')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <DeckCover
                    src={deck.coverImage}
                    emoji={deck.emoji}
                    color={deck.color}
                    fallbackIcon={<FiBookmark size={28} />}
                  />

                  <div className="browse-card-content">
                    <h3 className="browse-card-name">{deck.name}</h3>
                    <p className="browse-card-desc">{deck.meta}</p>
                    <button
                      className="browse-use-btn"
                      onClick={e => { e.stopPropagation(); navigate(deck.route); }}
                    >
                      {deck.source === 'custom' ? t('lib.useThisDeck') : t('lib.startSession')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

function isNew(deck) {
  const raw = deck.createdAt;
  if (!raw) return false;
  const ts = raw?.toDate ? raw.toDate() : raw?.seconds ? new Date(raw.seconds * 1000) : new Date(raw);
  return Date.now() - ts.getTime() < TWENTY_FOUR_HOURS_MS;
}
